import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION).matches;
}

/** Live `prefers-reduced-motion`. `false` on the server and on the first client render (no mismatch). */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia(REDUCED_MOTION);
    const sync = () => setReduced(query.matches);
    sync();
    query.addEventListener('change', sync);
    return () => query.removeEventListener('change', sync);
  }, []);
  return reduced;
}

/** Whether the tab is visible: every loop on the page stops while it is not. */
export function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const sync = () => setVisible(document.visibilityState !== 'hidden');
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, []);
  return visible;
}

interface OnScreenOptions {
  threshold?: number;
  rootMargin?: string;
}

/**
 * The source's `onScreen(el, cb, threshold)`: true while at least `threshold` of the element is in view.
 * Without IntersectionObserver the element counts as always on screen.
 */
export function useOnScreen<T extends Element>(ref: RefObject<T>, { threshold = 0, rootMargin = '0px' }: OnScreenOptions = {}): boolean {
  const [onScreen, setOnScreen] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    if (typeof IntersectionObserver !== 'function') {
      setOnScreen(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => setOnScreen(entry.isIntersecting && entry.intersectionRatio >= threshold));
      },
      { threshold: [0, threshold, 0.5], rootMargin }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, threshold, rootMargin]);
  return onScreen;
}

/**
 * Scroll reveal for `[data-reveal]` and the direct children of `[data-reveal-group]` inside `rootRef`
 * (staggered through `--reveal-i`). Once visible, always visible. Under reduced motion, or without
 * IntersectionObserver, everything shows at once. The class is written imperatively because the
 * revealed elements are plain markup whose `className` React never changes.
 */
export function useScrollReveal(rootRef: RefObject<HTMLElement>): void {
  const done = useRef(false);
  useEffect(() => {
    const root = rootRef.current;
    if (!root || done.current) return undefined;
    const items: HTMLElement[] = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'));
    root.querySelectorAll<HTMLElement>('[data-reveal-group]').forEach((group) => {
      Array.from(group.children).forEach((child, index) => {
        if (!(child instanceof HTMLElement)) return;
        if (!child.style.getPropertyValue('--reveal-i')) child.style.setProperty('--reveal-i', String(index));
        items.push(child);
      });
    });
    const show = (element: Element) => element.classList.add('is-visible');
    if (typeof IntersectionObserver !== 'function' || prefersReducedMotion()) {
      items.forEach(show);
      done.current = true;
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          show(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -12% 0px' }
    );
    items.forEach((item) => observer.observe(item));
    // One delayed sweep: anything already on screen that the observer has not reported is shown.
    const sweep = window.setTimeout(() => {
      items.forEach((item) => {
        const box = item.getBoundingClientRect();
        if (box.bottom > 0 && box.top < window.innerHeight) show(item);
      });
    }, 900);
    return () => {
      observer.disconnect();
      window.clearTimeout(sweep);
    };
  }, [rootRef]);
}
